import React, { useState } from 'react';
import { pricingData, ctaLink } from '../data/mock';
import { Check, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const PricingSection = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="pricing" className="relative py-24 lg:py-32">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/30 to-slate-950" />
      <div className="absolute top-1/2 right-0 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[150px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-sm font-medium text-cyan-400 mb-6">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Start Free, Scale When{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
              Ready
            </span>
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Flexible plans for every stage of your AI visibility journey
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-1.5 bg-white/5 border border-white/10 rounded-full">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                !isAnnual
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isAnnual
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Annual
              <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
                Save 15%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {pricingData.map((plan, index) => {
            const price = isAnnual
              ? Math.round(parseInt(plan.price.replace('$', '')) * 0.85)
              : plan.price.replace('$', '');

            return (
              <div
                key={index}
                className={`relative group ${
                  plan.popular ? 'md:-mt-4 md:mb-4' : ''
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <div className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full text-sm font-semibold text-slate-950">
                      <Sparkles className="w-4 h-4" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div
                  className={`h-full p-8 rounded-2xl transition-all duration-500 ${
                    plan.popular
                      ? 'bg-gradient-to-b from-cyan-500/10 to-teal-500/5 border-2 border-cyan-500/30 shadow-2xl shadow-cyan-500/10'
                      : 'bg-white/[0.03] border border-white/10 hover:border-cyan-500/30'
                  }`}
                >
                  {/* Plan Name */}
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-sm text-slate-400 mb-6">{plan.description}</p>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl lg:text-5xl font-bold text-white">
                      ${price}
                    </span>
                    <span className="text-slate-400">{plan.period}</span>
                  </div>

                  {/* CTA Button */}
                  <a href={ctaLink} target="_blank" rel="noopener noreferrer" className="block mb-8">
                    <Button
                      className={`w-full py-6 font-semibold transition-all duration-300 ${
                        plan.popular
                          ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-slate-950 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-105'
                          : 'bg-white/10 hover:bg-white/20 text-white border border-white/10 hover:border-white/20'
                      }`}
                    >
                      {plan.ctaText}
                    </Button>
                  </a>

                  {/* Features */}
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-cyan-400" />
                        </div>
                        <span className="text-sm text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Free Trial Badge */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 rounded-full">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-slate-300 text-sm">
              Start with a <strong className="text-white">7-day free trial</strong> on the Essentials plan
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
